import { SlideOut } from '@interstate/components/SlideOut';
import { BreakPoint } from '@makemydeal/dr-activities-common';
import type { CommonConsumer } from '@makemydeal/dr-platform-types';
import { dealerSelectors } from '@makemydeal/dr-shared-store';
import { offerSessionUtils } from '@makemydeal/dr-shared-ui-utils';
import type { FC } from 'react';
import { Suspense, useState } from 'react';
import { useMediaQuery } from 'react-responsive';
import { useSelector } from 'react-redux';
import { CustomerEditModule } from './CustomerEditModule';
import { FindOrCreateModule } from './FindOrCreateModule';
import { RemoteModuleErrorBoundary } from './RemoteModuleErrorBoundary';
import { useCustomerCardJwtToken } from './useCustomerCardJwtToken';
import { COMMON_CONSUMER_REQUIRED_FIELDS, CUSTOMER_REQUIRED_FIELDS_FOR_PBC } from './types/CustomerSideBarRequiredFields';
import type { CustomerSearchProps } from './types/CustomerSearchProps';

type CustomerSearchState = {
    consumer?: CommonConsumer;
    showCustomer: boolean;
};

export const isNullOrEmpty = (value: unknown) => {
    if (value == null) return true;
    if (typeof value === 'string') {
        return value.trim().length === 0;
    }
    if (typeof value === 'object') {
        return Object.keys(value).length === 0;
    }
    return false;
};

export const CustomerSearch: FC<CustomerSearchProps> = ({ show, setShow, onCustomerSelected, commonOrgId }) => {
    const [searchState, setSearchState] = useState<CustomerSearchState>({ showCustomer: false });
    const isLargerThanSmall = useMediaQuery({ query: `(min-width: ${BreakPoint.SMALL})` });
    const viewportMode = isLargerThanSmall ? 'desktop' : 'mobile';

    const environment = offerSessionUtils.getEnvironmentType();
    const getFindOrCreateJwtToken = useCustomerCardJwtToken('FindOrCreateModule');
    const getCustomerEditJwtToken = useCustomerCardJwtToken('CustomerEditModule');
    // Sourced for New Relic enrichment so MV:CCC:ModuleRenderError events on
    // the "MV — Common Consumer Card Integration Health" dashboard can be
    // sliced / drilled-into per dealer (string-cast for stable NRQL FACET grouping).
    const dealerId = useSelector(dealerSelectors.getDealerId);
    const dealerIdAttr = dealerId != null ? String(dealerId) : '';
    const handleCloseSlider = () => {
        setShow(false);
        resetSearchState();
    };

    const resetSearchState = () => {
        setSearchState((_) => ({ showCustomer: false, customer: undefined }));
    };

    const localHandleCustomerSelected = (consumer?: CommonConsumer) => {
        if (consumer == null) return handleCloseSlider(); // edit was cancelled; just close the slide out
        if (
            COMMON_CONSUMER_REQUIRED_FIELDS.some((field) =>
                isNullOrEmpty(consumer[field as keyof CommonConsumer] as string | undefined)
            )
        ) {
            // NOTE: search or edit finished with an updated consumer; but the consumer is still missing fields.
            setSearchState({ consumer: consumer, showCustomer: true });
            return;
        }
        resetSearchState(); // onhide doesn't call when edit customer saved

        // NOTE: happy path, consumer is complete. Pass it along to the consuming component.
        onCustomerSelected(consumer);
    };

    return (
        <SlideOut
            show={show}
            header={''}
            data-testid="customer-search-drawer"
            position="right"
            panelWidth="auto"
            sx={{
                '.MuiPaper-root': {
                    width: { xs: 1, md: 584 }
                },
                '.interstate-close-sliding-drawer-button ': {
                    margin: '1rem'
                },
                '.interstate-sliding-drawer-dialog-title-container': {
                    'padding-top': 0,
                    'padding-bottom': 0
                },
                '.MuiDialogContent-root': {
                    'padding-bottom': '1rem'
                },
                '#sliding-drawer-footer-actions': {
                    display: 'none'
                },
                '#customer-form-alert-banner': {
                    maxWidth: '100% !important',
                    width: '100% !important'
                }
            }}
            onHide={handleCloseSlider}
        >
            <Suspense fallback={<div>Loading...</div>}>
                {!searchState.showCustomer && (
                    <RemoteModuleErrorBoundary moduleName="FindOrCreateModule" dealerId={dealerIdAttr}>
                        <FindOrCreateModule
                            onFindOrCreate={localHandleCustomerSelected}
                            getAuthOverride={getFindOrCreateJwtToken}
                            environment={environment}
                            dealershipId={commonOrgId} // NOTE: use "CoOrgTestId1" for debugging locally.
                            viewportMode={viewportMode}
                            requiredFields={CUSTOMER_REQUIRED_FIELDS_FOR_PBC}
                        />
                    </RemoteModuleErrorBoundary>
                )}

                {/* NOTE: FYI: with state props bound together; showCustomer will never be true when consumer is null. */}
                {searchState.showCustomer && searchState.consumer != null && (
                    <RemoteModuleErrorBoundary
                        moduleName="CustomerEditModule"
                        commonConsumerId={searchState.consumer.consumerId}
                        dealerId={dealerIdAttr}
                    >
                        <CustomerEditModule
                            commonConsumer={searchState.consumer}
                            environment={environment}
                            getAuthOverride={getCustomerEditJwtToken}
                            didSaveOrDiscard={localHandleCustomerSelected}
                            requiredFields={CUSTOMER_REQUIRED_FIELDS_FOR_PBC}
                        />
                    </RemoteModuleErrorBoundary>
                )}
            </Suspense>
        </SlideOut>
    );
};
